import React from "react";
import { LogOut } from "lucide-react";
import { Button } from "./ui/button";
import { auth } from "../lib/api";

/**
 * Unified user-identity block + Logout button that every page mounts in
 * its header. Two `tone` variants for the two header backgrounds we use
 * across the app:
 *
 *   • `dark`  — teal/cyan/slate headers  (Home, MyPRs, PRReview,
 *               Dashboard, SapHub). White text, subtle white/10 hover.
 *   • `light` — white/slate headers      (rare, kept for future admin
 *               pages that adopt an all-white chrome).
 *
 * Renders nothing when there is no session — so it's safe to drop into
 * a header that may render before the user is loaded.
 */
export function UserMenu({ tone = "dark", showRole = true, className = "" }) {
  const user = auth.user();
  if (!user) return null;

  const role = user.role_name || user.role || "";
  const name = user.full_name || user.username || user.emp_no || "—";

  const isDark = tone === "dark";
  const labelColour  = isDark ? "text-white/70"     : "text-slate-500";
  const nameColour   = isDark ? "text-white"        : "text-slate-900";
  const btnClass     = isDark
    ? "text-white hover:bg-white/10"
    : "text-slate-700 hover:bg-slate-100";

  return (
    <div className={`flex items-center gap-2 sm:gap-3 ${className}`}
         data-testid="user-menu">
      <div className="text-right hidden sm:block leading-tight">
        {showRole && (
          <div className={`text-[10px] uppercase tracking-wider ${labelColour}`}>
            {role || "—"}
          </div>
        )}
        <div className={`text-xs font-semibold truncate max-w-[180px] ${nameColour}`}
             data-testid="user-menu-name" title={name}>
          {name}
        </div>
      </div>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => auth.logout()}
        className={btnClass}
        data-testid="user-menu-logout"
        aria-label="Log out"
      >
        <LogOut size={15} className="sm:mr-1" />
        <span className="hidden sm:inline">Logout</span>
      </Button>
    </div>
  );
}

export default UserMenu;
